import React, { useMemo } from 'react';

import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { SALT_CATEGORY_SHORT_LABELS, prettySaltCategory } from '../../../utils/SaltCategoryLabels';
import './saltBreakdown.css';

// Categories whose `list` entries are individual cards (keyed by card id)
// vs. derived aggregates like infiniteCombos / locks which key by combo id
// and don't merge with the deck's `cards` map.
const CARD_BACKED_CATS = new Set([
    'mld',
    'boardwipes',
    'taxes',
    'cedhMetashare',
    'sacrifice',
    'reserved',
    'cantActivate',
    'cantAttack',
    'cantCast',
    'cantDraw',
    'cantEven',
    'cantGainLife',
    'cantSearch',
    'cantTrigger',
    'cantUntap',
    'edhrec',
    'theft',
    'extraCombats',
    'extraTurns',
    'wheels',
    'offColorFetches',
    'cardPrice',
    'payTheOne',
    'discard',
    'poison',
    'chaos',
    'groupSlug',
    'annihilator',
]);

const CATEGORY_SHORT_LABELS = SALT_CATEGORY_SHORT_LABELS;

const TIERS = [
    { min: 60, key: 'extreme', label: 'Extreme' },
    { min: 20, key: 'high', label: 'High' },
    { min: 5, key: 'moderate', label: 'Moderate' },
    { min: 0, key: 'mild', label: 'Mild' },
];

function getTier(score) {
    for (const t of TIERS) if (score >= t.min) return t;
    return TIERS[TIERS.length - 1];
}

// Personality classifier — scans the category shares to tag the deck's
// salt "flavor". Returns a ranked list (usually 1 primary, optional
// secondary) so players can name their own deck's salt personality.
const PERSONALITY_RULES = [
    { key: 'mld', label: 'Earth-Scorcher', hint: 'Mass Land Destruction is the primary salt driver.', match: (s) => s.mld >= 0.3 },
    {
        key: 'stax',
        label: 'Stax Overlord',
        hint: 'Your \u201ccan\u2019t do anything\u201d effects dominate.',
        match: (s) => s.stax >= 0.25,
    },
    {
        key: 'combo',
        label: 'Combo Assassin',
        hint: 'Infinite combos make up a large share of the salt.',
        match: (s) => s.infiniteCombos >= 0.2,
    },
    {
        key: 'wipes',
        label: 'Reset Button',
        hint: 'Boardwipes + sweepers drive the salt.',
        match: (s) => s.boardwipes >= 0.2,
    },
    {
        key: 'reservedPrice',
        label: 'Wallet Flex',
        hint: 'Reserved-list + expensive cards carry the salt load.',
        match: (s) => s.reserved + s.cardPrice >= 0.25,
    },
    {
        key: 'wheelsDiscard',
        label: 'Discard Dom',
        hint: 'Wheels and discard dominate the salt profile.',
        match: (s) => s.wheels + s.discard >= 0.2,
    },
    { key: 'theft', label: 'Kleptomaniac', hint: 'Theft effects make up a meaningful share.', match: (s) => s.theft >= 0.1 },
    {
        key: 'extraCombats',
        label: 'Attack Step Addict',
        hint: 'Extra-combats + extra-turns drive the salt.',
        match: (s) => s.extraCombats + s.extraTurns >= 0.15,
    },
    {
        key: 'annihilator',
        label: 'Annihilator Abuser',
        hint: 'Eldrazi-style annihilator contributes noticeable salt.',
        match: (s) => s.annihilator >= 0.1,
    },
    {
        key: 'groupSlug',
        label: 'Pain Enthusiast',
        hint: 'Group-slug effects are a noticeable share.',
        match: (s) => s.groupSlug >= 0.1,
    },
];

function classifyPersonality(shares) {
    const hits = PERSONALITY_RULES.filter((rule) => rule.match(shares));
    if (hits.length === 0) {
        return [
            {
                key: 'balanced',
                label: 'Balanced Salt',
                hint: 'No single category dominates. The salt is spread across many categories.',
            },
        ];
    }
    if (hits.length >= 3) {
        return [
            { key: 'stacked', label: 'Stacked Spite', hint: 'Multiple salt categories are all pulling serious weight.' },
            ...hits.slice(0, 2),
        ];
    }
    return hits.slice(0, 2);
}

function formatScore(n, digits = 1) {
    if (!Number.isFinite(n)) return '—';
    return n.toFixed(digits);
}

const prettyCategoryLabel = prettySaltCategory;

/**
 * Rich salt breakdown surfaces beneath the iconic nail chart. Consumes the
 * same `categories` map SaltPane computes and the deck's total salt score.
 */
export function SaltBreakdown({ categories, deckTotal }) {
    const openCardInfo = useOpenCardInfo();

    const { cardSalt, stackable, categoryStats, topOffenders, totalsByCat } = useMemo(
        () => buildSaltModel(categories),
        [categories]
    );

    if (!categoryStats.length) return null;

    const totalSalt = Number.isFinite(deckTotal) && deckTotal > 0
        ? deckTotal
        : categoryStats.reduce((acc, c) => acc + c.total, 0);

    // Shares are normalised against the deck's reported total so `mld: 0.28`
    // means MLD is 28% of this deck's salt. Guards against divide-by-zero.
    const shares = buildShares(totalsByCat, totalSalt);
    const personalities = classifyPersonality(shares);

    // "Drop top N" — what % of salt disappears if you swap out the top
    // three contributors? Clamps N at whatever's available.
    const topN = Math.min(3, topOffenders.length);
    const swapSavings = topOffenders.slice(0, topN).reduce((acc, c) => acc + c.total, 0);
    const swapPercent = totalSalt > 0 ? (swapSavings / totalSalt) * 100 : 0;

    return (
        <div className="saltBreakdown">
            <PersonalityPanel personalities={personalities} totalSalt={totalSalt} />

            <div className="saltBreakdownGrid">
                {categoryStats.map((cat) => (
                    <CategoryTile
                        key={cat.id}
                        cat={cat}
                        totalSalt={totalSalt}
                        onSelectCard={openCardInfo}
                    />
                ))}
            </div>

            <div className="saltBreakdownColumns">
                <TopOffendersPanel
                    offenders={topOffenders.slice(0, 10)}
                    totalSalt={totalSalt}
                    onSelectCard={openCardInfo}
                />
                <StackablePanel stackable={stackable} onSelectCard={openCardInfo} />
            </div>

            <SwapImpactPanel
                topN={topN}
                savings={swapSavings}
                percent={swapPercent}
                cards={topOffenders.slice(0, topN)}
            />
        </div>
    );
}

// ---------- Data derivation ----------------------------------------------

function buildSaltModel(categories) {
    const cardSalt = new Map();
    const categoryStats = [];
    const totalsByCat = {};

    Object.keys(categories || {}).forEach((catId) => {
        const cat = categories[catId];
        const list = cat?.list || [];
        const total = list.reduce((acc, entry) => acc + Number(entry?.score || 0), 0);
        totalsByCat[catId] = total;
        if (!list.length) return;

        // Top single contributor for this category — the name we surface
        // in the stat tile so at-a-glance you see the dominant card.
        let topEntry = null;
        for (const entry of list) {
            if (!topEntry || Number(entry?.score || 0) > Number(topEntry?.score || 0)) topEntry = entry;
        }

        categoryStats.push({
            id: catId,
            label: cat?.label || prettyCategoryLabel(catId),
            count: list.length,
            total,
            topEntry,
            isCardBacked: CARD_BACKED_CATS.has(catId),
        });

        if (!CARD_BACKED_CATS.has(catId)) return;

        for (const entry of list) {
            const id = entry?.id;
            if (!id) continue;
            const score = Number(entry?.score || 0);
            if (!score) continue;
            const existing = cardSalt.get(id);
            if (existing) {
                existing.total += score;
                existing.breakdown.push({ category: catId, score });
            } else {
                cardSalt.set(id, {
                    id,
                    name: entry.name || id,
                    imageUri: entry.imageUri,
                    types: entry.types,
                    isFrontFace: entry.isFrontFace,
                    total: score,
                    breakdown: [{ category: catId, score }],
                });
            }
        }
    });

    categoryStats.sort((a, b) => b.total - a.total);

    const topOffenders = Array.from(cardSalt.values())
        .map((c) => ({ ...c, breakdown: c.breakdown.slice().sort((a, b) => b.score - a.score) }))
        .sort((a, b) => b.total - a.total);

    const stackable = topOffenders
        .filter((c) => c.breakdown.length >= 2)
        .slice(0, 12);

    return { cardSalt, stackable, categoryStats, topOffenders, totalsByCat };
}

function buildShares(totalsByCat, totalSalt) {
    const safe = (n) => (totalSalt > 0 ? n / totalSalt : 0);
    const stax =
        (totalsByCat.cantActivate || 0) +
        (totalsByCat.cantAttack || 0) +
        (totalsByCat.cantCast || 0) +
        (totalsByCat.cantDraw || 0) +
        (totalsByCat.cantEven || 0) +
        (totalsByCat.cantGainLife || 0) +
        (totalsByCat.cantSearch || 0) +
        (totalsByCat.cantTrigger || 0) +
        (totalsByCat.cantUntap || 0) +
        (totalsByCat.taxes || 0);
    return {
        mld: safe(totalsByCat.mld || 0),
        boardwipes: safe(totalsByCat.boardwipes || 0),
        stax: safe(stax),
        infiniteCombos: safe(totalsByCat.infiniteCombos || 0),
        reserved: safe(totalsByCat.reserved || 0),
        cardPrice: safe(totalsByCat.cardPrice || 0),
        wheels: safe(totalsByCat.wheels || 0),
        discard: safe(totalsByCat.discard || 0),
        theft: safe(totalsByCat.theft || 0),
        extraCombats: safe(totalsByCat.extraCombats || 0),
        extraTurns: safe(totalsByCat.extraTurns || 0),
        annihilator: safe(totalsByCat.annihilator || 0),
        groupSlug: safe(totalsByCat.groupSlug || 0),
    };
}

// ---------- UI fragments --------------------------------------------------

function PersonalityPanel({ personalities, totalSalt }) {
    const primary = personalities[0];
    const rest = personalities.slice(1);
    return (
        <section className="saltPersonality">
            <div className="saltPersonalityMain">
                <span className="saltPersonalityEyebrow">Salt personality</span>
                <span className="saltPersonalityName">{primary.label}</span>
                <span className="saltPersonalityHint">{primary.hint}</span>
            </div>
            <div className="saltPersonalityRight">
                <div className="saltPersonalityTotalBox">
                    <span className="saltPersonalityTotalLabel">Deck salt</span>
                    <span className="saltPersonalityTotalValue">{formatScore(totalSalt, 1)}</span>
                </div>
                {rest.length > 0 && (
                    <div className="saltPersonalityTags">
                        {rest.map((p) => (
                            <span key={p.key} className="saltPersonalityTag" title={p.hint}>
                                {p.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function CategoryTile({ cat, totalSalt, onSelectCard }) {
    const tier = getTier(cat.total);
    const pct = totalSalt > 0 ? (cat.total / totalSalt) * 100 : 0;
    const topName = cat.topEntry?.name || '—';
    const canClick = cat.isCardBacked && cat.topEntry && cat.topEntry.imageUri;
    const clickTop = () => {
        if (canClick && onSelectCard) onSelectCard(cat.topEntry);
    };
    return (
        <div className={`saltCatTile saltCatTile--${tier.key}`}>
            <div className="saltCatTileHead">
                <span className="saltCatTileLabel">{prettyCategoryLabel(cat.id)}</span>
                <span className={`saltCatTileTier saltCatTileTier--${tier.key}`}>{tier.label}</span>
            </div>
            <div className="saltCatTileMain">
                <span className="saltCatTileScore">{formatScore(cat.total, 1)}</span>
                <span className="saltCatTileScoreUnit">salt</span>
            </div>
            <div className="saltCatTileMeta">
                <span className="saltCatTileCount">
                    {cat.count} {cat.count === 1 ? 'entry' : 'entries'}
                </span>
                <span className="saltCatTilePercent">{pct.toFixed(0)}% of deck</span>
            </div>
            <div className="saltCatTileTopRow">
                <span className="saltCatTileTopLabel">Top</span>
                <button
                    type="button"
                    className={`saltCatTileTopBtn${canClick ? '' : ' is-static'}`}
                    onClick={clickTop}
                    disabled={!canClick}
                    title={canClick ? `View ${topName}` : topName}
                >
                    <span className="saltCatTileTopName">{topName}</span>
                    <span className="saltCatTileTopScore">{formatScore(Number(cat.topEntry?.score || 0), 1)}</span>
                </button>
            </div>
        </div>
    );
}

function TopOffendersPanel({ offenders, totalSalt, onSelectCard }) {
    if (!offenders.length) return null;
    return (
        <section className="saltSubPanel saltSubPanel--offenders">
            <header className="saltSubPanelHead">
                <span className="saltSubPanelEyebrow">Top offenders</span>
                <span className="saltSubPanelHint">Highest total salt across all categories.</span>
            </header>
            <ol className="saltOffenderList">
                {offenders.map((c, idx) => {
                    const canClick = !!c.imageUri;
                    const pctOfDeck = totalSalt > 0 ? (c.total / totalSalt) * 100 : 0;
                    return (
                        <li key={c.id} className="saltOffender">
                            <span className="saltOffenderRank">{idx + 1}</span>
                            <button
                                type="button"
                                className="saltOffenderCardBtn"
                                onClick={() => canClick && onSelectCard(c)}
                                disabled={!canClick}
                            >
                                {c.imageUri ? (
                                    <img src={c.imageUri} alt={c.name} className="saltOffenderThumb" loading="lazy" />
                                ) : (
                                    <span className="saltOffenderThumb saltOffenderThumb--empty" />
                                )}
                                <span
                                    className="saltOffenderName"
                                    style={{ color: c.isFrontFace === false ? '#fff49a' : '#ffffff' }}
                                >
                                    {c.name}
                                </span>
                            </button>
                            <div className="saltOffenderMeta">
                                <div className="saltOffenderBarRow">
                                    <div className="saltOffenderBar">
                                        <div className="saltOffenderBarFill" style={{ width: '100%' }} />
                                    </div>
                                    <span className="saltOffenderScore">{formatScore(c.total, 1)}</span>
                                </div>
                                <div className="saltOffenderBreakdown">
                                    {c.breakdown.slice(0, 5).map((b) => (
                                        <span key={b.category} className="saltOffenderChip" title={`${prettyCategoryLabel(b.category)} · ${formatScore(b.score, 2)}`}>
                                            <span className="saltOffenderChipLabel">{prettyCategoryLabel(b.category)}</span>
                                            <span className="saltOffenderChipScore">{formatScore(b.score, 1)}</span>
                                        </span>
                                    ))}
                                    {c.breakdown.length > 5 && (
                                        <span className="saltOffenderMore">+{c.breakdown.length - 5}</span>
                                    )}
                                </div>
                                <span className="saltOffenderDeckPct">{pctOfDeck.toFixed(1)}% of deck salt</span>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}

function StackablePanel({ stackable, onSelectCard }) {
    if (!stackable.length) {
        return (
            <section className="saltSubPanel saltSubPanel--stackable">
                <header className="saltSubPanelHead">
                    <span className="saltSubPanelEyebrow">Stackable offenders</span>
                    <span className="saltSubPanelHint">Cards appearing in 2+ categories.</span>
                </header>
                <div className="saltSubPanelEmpty">
                    None detected — your salt is spread across single-category cards.
                </div>
            </section>
        );
    }
    return (
        <section className="saltSubPanel saltSubPanel--stackable">
            <header className="saltSubPanelHead">
                <span className="saltSubPanelEyebrow">Stackable offenders</span>
                <span className="saltSubPanelHint">Appear in multiple salt categories — doubly salty.</span>
            </header>
            <div className="saltStackableList">
                {stackable.map((c) => {
                    const canClick = !!c.imageUri;
                    return (
                        <button
                            key={c.id}
                            type="button"
                            className="saltStackableRow"
                            onClick={() => canClick && onSelectCard(c)}
                            disabled={!canClick}
                        >
                            {c.imageUri ? (
                                <img src={c.imageUri} alt={c.name} className="saltStackableThumb" loading="lazy" />
                            ) : (
                                <span className="saltStackableThumb saltStackableThumb--empty" />
                            )}
                            <div className="saltStackableBody">
                                <div className="saltStackableTopLine">
                                    <span
                                        className="saltStackableName"
                                        style={{ color: c.isFrontFace === false ? '#fff49a' : '#ffffff' }}
                                    >
                                        {c.name}
                                    </span>
                                    <span className="saltStackableCount">
                                        {c.breakdown.length} cats · {formatScore(c.total, 1)}
                                    </span>
                                </div>
                                <div className="saltStackableChips">
                                    {c.breakdown.map((b) => (
                                        <span key={b.category} className="saltStackableChip">
                                            {prettyCategoryLabel(b.category)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}

function SwapImpactPanel({ topN, savings, percent, cards }) {
    if (topN === 0 || savings <= 0) return null;
    return (
        <section className="saltSwapPanel">
            <div className="saltSwapHeader">
                <span className="saltSwapEyebrow">Swap impact</span>
                <span className="saltSwapHint">Dropping your top offenders cuts this much salt.</span>
            </div>
            <div className="saltSwapBody">
                <div className="saltSwapStat">
                    <span className="saltSwapStatLabel">Drop top {topN}</span>
                    <span className="saltSwapStatValue">&minus;{percent.toFixed(1)}%</span>
                    <span className="saltSwapStatSub">({formatScore(savings, 1)} salt)</span>
                </div>
                <div className="saltSwapCards">
                    {cards.map((c, idx) => (
                        <span key={c.id} className="saltSwapCard">
                            <span className="saltSwapCardRank">{idx + 1}</span>
                            <span
                                className="saltSwapCardName"
                                style={{ color: c.isFrontFace === false ? '#fff49a' : '#ffffff' }}
                            >
                                {c.name}
                            </span>
                            <span className="saltSwapCardScore">&minus;{formatScore(c.total, 1)}</span>
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
}
